import { AccountSelector } from '@/design-system/components/accountSelector/AccountSelector';
import { Dropdown } from '@/design-system/components/dropdown';
import { UniInput } from '@/design-system/components/inputs';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { iframePostMessage } from '@/utils/iframePostMessage';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { Pressable, SxProp, View } from 'dripsy';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { UseBillPaymentFormProp } from '../../hooks/useBillPaymentForm.hook';
import { toNumber } from '../../utils/utils';

export interface DetailFormMobileComponentProps {
  sx?: SxProp;
  billPaymentProp: UseBillPaymentFormProp;
}

export const DetailFormMobileComponent: React.FC<DetailFormMobileComponentProps> = ({ sx, billPaymentProp }) => {
  const { t } = useTranslation('global-bill-payment');
  const {
    detailForm: {
      assetName,
      assetList,
      walletId,
      bank,
      amount,
      bankAccountNumber,
      additionalDetail,
      setBankName,
      setBankAccountNumber,
      setDebitAccountWallerId,
      setAdditionalDetail,
      setAssetName,
      setAmount,
      setWalletId,
      setDebitAccountWalletName
    },
    scheduleForm: { remarks, setRemarks, onValidateScheduleForm },
    general: { billPaymentState }
  } = billPaymentProp;

  const onAssetChange = (value: string) => {
    setAssetName(value);
    setDebitAccountWallerId(undefined);
    setDebitAccountWalletName(undefined);
  };

  const renderErrorAmountLayout = () => {
    if (billPaymentState.amount?.error) {
      const number = toNumber(amount);
      if (number <= 0) {
        return (
          <>
            <TextCaption danger sx={{ mt: -12 }}>
              {t('global-bill-payment::instruction.text--greater-than-zero')}
            </TextCaption>
          </>
        );
      } else {
        return (
          <>
            <TextCaption danger sx={{ mt: -12 }}>
              {t('global-bill-payment::payment-details.text--amount- insufficient')}
              {t('global-bill-payment::payment-details.text--inject-funds-3')}
              {t('global-bill-payment::payment-details.text--inject-funds-1')}{' '}
              <Pressable onPress={() => iframePostMessage('showInjectionInstruction', true)}>
                <TextCaption danger semiBold={true} sx={sxStyles.textUnderline}>
                  {t('global-bill-payment::instruction.button--injection')}
                </TextCaption>
              </Pressable>{' '}
              {t('global-bill-payment::payment-details.text--inject-funds-2')}{' '}
              <Pressable onPress={() => iframePostMessage('moveToExchange', true)}>
                <TextCaption danger semiBold={true} sx={sxStyles.textUnderline}>
                  {t('global-bill-payment::instruction.button--exchange')}
                </TextCaption>
              </Pressable>
              .
            </TextCaption>
          </>
        );
      }
    } else return <></>;
  };

  return (
    <View sx={{ ...sxStyles.container, ...sx }}>
      <View sx={sxStyles.leftComponent}>
        <View sx={sxStyles.number}>
          <TextBody sx={sxStyles.textNumber}>2</TextBody>
        </View>
        <TextTitle semiBold={true}>{t('global-bill-payment::instruction.text--enter-details')}</TextTitle>
      </View>
      <View sx={sxStyles.rightComponent}>
        <View sx={sxStyles.merchantWrapper}>
          <UniInput
            value={bank}
            labelText={t('global-bill-payment::glossary.merchant-bank-name')}
            placeholder="Super Merchant Ltd."
            errorText={billPaymentState.bank?.message}
            onValueChange={(value) => {
              setBankName(value);
            }}
          />
        </View>
        <View sx={sxStyles.inputWrapper}>
          <UniInput
            value={bankAccountNumber}
            labelText={t('global-bill-payment::glossary.bill-bank-account-number')}
            placeholder="ABC1234567890"
            onValueChange={(value) => {
              setBankAccountNumber(value);
            }}
            errorText={billPaymentState.bankAccountNumber?.message}
          />
        </View>
        <View sx={sxStyles.inputWrapper}>
          <UniInput
            value={additionalDetail}
            labelText={t('global-bill-payment::glossary.payment-additional-details-optional')}
            placeholder={t('global-bill-payment::glossary.please-enter-details')}
            onValueChange={(value) => {
              setAdditionalDetail(value);
            }}
            textAlignVertical="top"
            multiline
            sx={sxStyles.viewInputOptional}
            inputSx={sxStyles.inputOptional}
          />
        </View>
        <View sx={sxStyles.mobileLayout}>
          <View sx={sxStyles.billAmountWrapper}>
            <Dropdown
              sx={{ ...sxStyles.mobileCode, ...{ borderColor: billPaymentState.amount?.error ? '$danger' : '$borderButton' } }}
              onValueChange={onAssetChange}
              options={assetList}
              value={assetName}
              label={t('global-bill-payment::glossary.bill-amount')}
              placeholder="Asset"
            />
          </View>
          <View sx={sxStyles.amountInputWrapper}>
            <UniInput
              value={amount}
              labelText=" "
              placeholder="0"
              defaultValue={amount}
              errorText={billPaymentState.amount?.error ? ' ' : undefined}
              keyboardType={'numeric'}
              onValueChange={(value) => {
                setAmount(formatAmountByDecimals({ value: toNumber(value) }));
              }}
            />
          </View>
        </View>
        {renderErrorAmountLayout()}
        <View sx={sxStyles.viewDebitAccount}>
          <TextCaption semiBold sx={sxStyles.textDebitAccount}>
            {t('global-bill-payment::glossary.debit-amount')}
          </TextCaption>
          <AccountSelector
            asset={assetName}
            type={'currency'}
            onValueChange={setWalletId}
            value={walletId}
            onWalletChange={(value) => {
              if (value) {
                setDebitAccountWallerId(value.accountNumber);
                setDebitAccountWalletName(value.label);
                billPaymentState.wallet?.error && onValidateScheduleForm();
              }
            }}
            sx={{
              mb: billPaymentState.wallet?.error ? '$1' : '$5'
            }}
            showBalance={true}
            disabledForwardBrowser={true}
          />
          {billPaymentState.wallet?.error && (
            <TextCaption danger sx={sxStyles.inputWrapper}>
              {billPaymentState.wallet?.message}
            </TextCaption>
          )}
          <View sx={sxStyles.viewRemarkWrapper}>
            <UniInput
              labelText={t('global-bill-payment::general.remarks-option')}
              sx={sxStyles.inputRemarkWrapper}
              inputSx={sxStyles.inputRemark}
              textAlignVertical="top"
              multiline
              onValueChange={setRemarks}
              value={remarks}
            />
          </View>
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: 'transparent',
    flexDirection: 'column',
    width: '100%'
  },
  leftComponent: {
    py: '$1',
    pl: '$8',
    flexDirection: 'row',
    justifyItems: 'center'
  },
  rightComponent: {
    flexDirection: 'column',
    mt: '$1'
  },
  number: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '$6',
    height: '$6',
    borderRadius: '$radius8',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '$secondary'
  },
  textNumber: {
    pt: Platform.OS != 'web' ? 2 : '$0',
    color: '$white'
  },
  mobileLayout: {
    width: '100%',
    flexDirection: 'row',
    zIndex: 1000
  },
  mobileCode: {
    width: 100,
    height: '$11',
    mr: 8
  },
  viewDebitAccount: {
    zIndex: 500,
    mt: '$5',
    mb: -20
  },
  textDebitAccount: {
    mb: '$1'
  },
  inputWrapper: {
    mb: '$5'
  },
  merchantWrapper: {
    mb: '$5',
    mt: '$2'
  },
  viewInputOptional: {
    height: '$17'
  },
  inputOptional: {
    height: '100%',
    pt: '$4'
  },
  billAmountWrapper: {
    flexDirection: 'column',
    zIndex: 1000
  },
  amountInputWrapper: {
    flex: 1
  },
  inputRemarkWrapper: {
    height: '$17'
  },
  inputRemark: {
    height: '100%',
    pt: '$4'
  },
  textUnderline: {
    textDecorationLine: 'underline'
  }
};
